import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WalletIcon, PlusIcon, TrendingUpIcon, ClockIcon, CheckCircleIcon, AlertCircleIcon, ArrowDownIcon, ArrowUpIcon, GiftIcon, RotateCcwIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useAuth } from '../../lib/AuthContext';
import { formatVND, isValidDepositAmount, calculateBonus } from '../../lib/formatters';
import { MOCK_WALLET_TRANSACTIONS } from '../../lib/mockData';
import type { WalletTransaction } from '../../lib/types';
const QUICK_AMOUNTS = [10000000, 20000000, 50000000, 100000000];
export function WalletPage() {
  const {
    currentCustomer,
    isCustomerLoggedIn,
    updateCustomer
  } = useAuth();
  const [depositAmount, setDepositAmount] = useState<number>(0);
  const [customAmount, setCustomAmount] = useState('');
  const [depositError, setDepositError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  if (!isCustomerLoggedIn || !currentCustomer) {
    return <div className="max-w-4xl mx-auto px-4 py-16 text-center" data-id="element-1704">
        <WalletIcon className="w-16 h-16 text-[#666666] mx-auto mb-4" data-id="element-1705" />
        <h2 className="text-xl font-semibold text-white mb-2" data-id="element-1706">
          Vui lòng đăng nhập
        </h2>
        <p className="text-[#B0B0B0]" data-id="element-1707">Đăng nhập để xem ví của bạn</p>
      </div>;
  }
  const transactions = MOCK_WALLET_TRANSACTIONS.filter(t => t.customerId === currentCustomer.id);
  const bonus = calculateBonus(depositAmount);
  const handleQuickSelect = (amount: number) => {
    setDepositAmount(amount);
    setCustomAmount('');
    setDepositError('');
  };
  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    const num = parseInt(value.replace(/\D/g, ''), 10);
    if (isNaN(num) || num === 0) {
      setDepositAmount(0);
      setDepositError('');
      return;
    }
    setDepositAmount(num);
    if (!isValidDepositAmount(num)) {
      setDepositError('Số tiền nạp phải theo bội số 10.000.000đ');
    } else {
      setDepositError('');
    }
  };
  const handleDeposit = () => {
    if (!isValidDepositAmount(depositAmount)) {
      setDepositError('Số tiền nạp phải theo bội số 10.000.000đ');
      return;
    }
    setShowConfirm(true);
  };
  const confirmDeposit = () => {
    setIsProcessing(true);
    setTimeout(() => {
      updateCustomer({
        walletBalance: currentCustomer.walletBalance + depositAmount + bonus,
        totalDeposited: currentCustomer.totalDeposited + depositAmount,
        totalBonusEarned: currentCustomer.totalBonusEarned + bonus
      });
      setIsProcessing(false);
      setShowConfirm(false);
      setShowSuccess(true);
      setDepositAmount(0);
      setCustomAmount('');
      setTimeout(() => setShowSuccess(false), 3000);
    }, 1500);
  };
  const getTransactionIcon = (type: WalletTransaction['type']) => {
    switch (type) {
      case 'deposit':
        return <ArrowDownIcon className="w-4 h-4 text-[#22C55E]" data-id="element-1708" />;
      case 'purchase':
        return <ArrowUpIcon className="w-4 h-4 text-[#EF4444]" data-id="element-1709" />;
      case 'bonus':
        return <GiftIcon className="w-4 h-4 text-[#C8A84E]" data-id="element-1710" />;
      case 'refund':
        return <RotateCcwIcon className="w-4 h-4 text-[#3B82F6]" data-id="element-1711" />;
      case 'expiry':
        return <ClockIcon className="w-4 h-4 text-[#F59E0B]" data-id="element-1712" />;
    }
  };
  const getTransactionLabel = (type: WalletTransaction['type']) => {
    switch (type) {
      case 'deposit':
        return 'Nạp tiền';
      case 'purchase':
        return 'Thanh toán';
      case 'bonus':
        return 'Thưởng';
      case 'refund':
        return 'Hoàn tiền';
      case 'expiry':
        return 'Hết hạn';
    }
  };
  return <div className="max-w-4xl mx-auto px-4 py-8" data-id="element-1713">
      {/* Success Toast */}
      <AnimatePresence data-id="element-1714">
        {showSuccess && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-[#22C55E] text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2" data-id="element-1715">
            <CheckCircleIcon className="w-5 h-5" data-id="element-1716" />
            Nạp tiền thành công!
          </motion.div>}
      </AnimatePresence>

      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-1717">
        <h1 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-1718">
          <WalletIcon className="w-7 h-7 text-[#C8A84E]" data-id="element-1719" />
          Ví Mỹ Hòa Phát
        </h1>
      </motion.div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-id="element-1720">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.05
      }} className="md:col-span-2 bg-gradient-to-br from-[#C8A84E]/15 to-[#C8A84E]/5 border border-[#C8A84E]/20 rounded-xl p-6" data-id="element-1721">
          <p className="text-sm text-[#C8A84E] mb-1" data-id="element-1722">Số dư ví</p>
          <p className="text-3xl md:text-4xl font-bold text-white" data-id="element-1723">
            {formatVND(currentCustomer.walletBalance)}
          </p>
          <p className="text-xs text-[#666666] mt-2" data-id="element-1724">
            Hết hạn sau 12 tháng kể từ giao dịch cuối
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-6" data-id="element-1725">
          <p className="text-sm text-[#666666] mb-1" data-id="element-1726">Tổng thưởng nhận được</p>
          <p className="text-2xl font-bold text-[#22C55E]" data-id="element-1727">
            +{formatVND(currentCustomer.totalBonusEarned)}
          </p>
          <div className="flex items-center gap-1 mt-2" data-id="element-1728">
            <TrendingUpIcon className="w-3.5 h-3.5 text-[#22C55E]" data-id="element-1729" />
            <span className="text-xs text-[#22C55E]" data-id="element-1730">
              Từ {formatVND(currentCustomer.totalDeposited)} nạp
            </span>
          </div>
        </motion.div>
      </div>

      {/* Deposit Section */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.15
    }} data-id="element-1731">
        <Card title="Nạp Tiền Vào Ví" className="mb-8" data-id="element-1732">
          <p className="text-sm text-[#B0B0B0] mb-4" data-id="element-1733">
            Nạp tiền và nhận ngay{' '}
            <span className="text-[#C8A84E] font-semibold" data-id="element-1734">
              +2.5% tín dụng thưởng
            </span>
          </p>

          {/* Quick Select */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-id="element-1735">
            {QUICK_AMOUNTS.map(amount => <button key={amount} onClick={() => handleQuickSelect(amount)} className={`py-3 px-4 rounded-lg text-sm font-semibold border transition-all duration-200 ${depositAmount === amount ? 'border-[#C8A84E] bg-[#C8A84E]/10 text-[#C8A84E]' : 'border-[#2A2A2A] text-[#B0B0B0] hover:border-[#333333] hover:text-white'}`} data-id="element-1736">
                +{formatVND(amount)}
              </button>)}
          </div>

          {/* Custom Amount */}
          <Input label="Hoặc nhập số tiền khác" placeholder="Ví dụ: 30000000" value={customAmount} onChange={e => handleCustomAmountChange(e.target.value)} error={depositError} hint="Bội số 10.000.000đ" rightIcon={<span className="text-sm text-[#666666]" data-id="element-1738">đ</span>} data-id="element-1737" />

          {/* Preview */}
          {depositAmount > 0 && isValidDepositAmount(depositAmount) && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} className="mt-4 bg-[#141414] border border-[#2A2A2A] rounded-lg p-4" data-id="element-1739">
              <div className="flex items-center justify-between mb-2" data-id="element-1740">
                <span className="text-sm text-[#B0B0B0]" data-id="element-1741">Số tiền nạp</span>
                <span className="text-sm text-white font-medium" data-id="element-1742">
                  {formatVND(depositAmount)}
                </span>
              </div>
              <div className="flex items-center justify-between mb-2" data-id="element-1743">
                <span className="text-sm text-[#B0B0B0]" data-id="element-1744">Thưởng +2.5%</span>
                <span className="text-sm text-[#22C55E] font-medium" data-id="element-1745">
                  +{formatVND(bonus)}
                </span>
              </div>
              <div className="border-t border-[#2A2A2A] pt-2 mt-2 flex items-center justify-between" data-id="element-1746">
                <span className="text-sm font-semibold text-white" data-id="element-1747">
                  Tổng nhận
                </span>
                <span className="text-lg font-bold text-[#C8A84E]" data-id="element-1748">
                  {formatVND(depositAmount + bonus)}
                </span>
              </div>
            </motion.div>}

          <Button fullWidth className="mt-4" onClick={handleDeposit} disabled={depositAmount === 0 || !isValidDepositAmount(depositAmount)} data-id="element-1749">
            <PlusIcon className="w-4 h-4" data-id="element-1750" />
            Nạp Tiền
          </Button>
        </Card>
      </motion.div>

      {/* Transaction History */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} data-id="element-1751">
        <Card title="Lịch Sử Giao Dịch Ví" data-id="element-1752">
          {transactions.length === 0 ? <div className="text-center py-8 text-[#666666]" data-id="element-1753">
              <ClockIcon className="w-10 h-10 mx-auto mb-2 opacity-50" data-id="element-1754" />
              <p data-id="element-1755">Chưa có giao dịch nào</p>
            </div> : <div className="space-y-3" data-id="element-1756">
              {transactions.map((tx, i) => <motion.div key={tx.id} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.03
          }} className="flex items-center gap-3 py-3 border-b border-[#2A2A2A]/50 last:border-0" data-id="element-1757">
                  <div className="w-9 h-9 rounded-lg bg-[#141414] flex items-center justify-center flex-shrink-0" data-id="element-1758">
                    {getTransactionIcon(tx.type)}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1759">
                    <div className="flex items-center gap-2" data-id="element-1760">
                      <span className="text-sm font-medium text-white truncate" data-id="element-1761">
                        {tx.description}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5" data-id="element-1762">
                      <Badge variant={tx.type === 'purchase' ? 'error' : tx.type === 'bonus' ? 'gold' : tx.type === 'refund' ? 'info' : 'success'} size="sm" data-id="element-1763">
                        {getTransactionLabel(tx.type)}
                      </Badge>
                      <span className="text-xs text-[#666666]" data-id="element-1764">
                        {new Date(tx.createdAt).toLocaleDateString('vi-VN')}
                      </span>
                    </div>
                  </div>
                  <span className={`text-sm font-semibold flex-shrink-0 ${tx.amount >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}`} data-id="element-1765">
                    {tx.amount >= 0 ? '+' : ''}
                    {formatVND(tx.amount)}
                  </span>
                </motion.div>)}
            </div>}
        </Card>
      </motion.div>

      {/* Confirm Modal */}
      <Modal isOpen={showConfirm} onClose={() => setShowConfirm(false)} title="Xác Nhận Nạp Tiền" data-id="element-1766">
        <div className="space-y-4" data-id="element-1767">
          <div className="bg-[#141414] rounded-lg p-4 space-y-2" data-id="element-1768">
            <div className="flex justify-between" data-id="element-1769">
              <span className="text-sm text-[#B0B0B0]" data-id="element-1770">Số tiền nạp</span>
              <span className="text-sm font-medium text-white" data-id="element-1771">
                {formatVND(depositAmount)}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-1772">
              <span className="text-sm text-[#B0B0B0]" data-id="element-1773">Thưởng +2.5%</span>
              <span className="text-sm font-medium text-[#22C55E]" data-id="element-1774">
                +{formatVND(bonus)}
              </span>
            </div>
            <div className="border-t border-[#2A2A2A] pt-2 flex justify-between" data-id="element-1775">
              <span className="text-sm font-semibold text-white" data-id="element-1776">
                Tổng nhận
              </span>
              <span className="text-lg font-bold text-[#C8A84E]" data-id="element-1777">
                {formatVND(depositAmount + bonus)}
              </span>
            </div>
          </div>
          <div className="flex items-start gap-2 text-xs text-[#666666]" data-id="element-1778">
            <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-1779" />
            <span data-id="element-1780">
              Tín dụng không thể chuyển nhượng và có hiệu lực 12 tháng.
            </span>
          </div>
          <div className="flex gap-3" data-id="element-1781">
            <Button variant="ghost" fullWidth onClick={() => setShowConfirm(false)} data-id="element-1782">
              Hủy
            </Button>
            <Button fullWidth onClick={confirmDeposit} isLoading={isProcessing} data-id="element-1783">
              Xác Nhận Nạp
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}